import { useState, useEffect, useCallback } from "react";
import "./Hangman.css";
import { ALPHABET, HANGMAN_QUESTIONS } from "../../utils/constants";
import gallows0 from "../../assets/gallows/gallows-0.png";
import gallows1 from "../../assets/gallows/gallows-1.png";
import gallows2 from "../../assets/gallows/gallows-2.png";
import gallows3 from "../../assets/gallows/gallows-3.png";
import gallows4 from "../../assets/gallows/gallows-4.png";
import gallows5 from "../../assets/gallows/gallows-5.png";
import gallows6 from "../../assets/gallows/gallows-6.png";

// Import images
// const importImages = () => {
//   const images = {};
//   const importAll = (r) => {
//     r.keys().forEach((item) => {
//       console.log(item);
//       images[item.replace("./", "")] = r(item);
//     });
//     console.log(r);
//   };
//   importAll(
//     require.context("../../assets/gallows/", false, /\.(png|jpe?g|svg)$/)
//   );
//   return images;
// };

// const images = importImages();

const Hangman = () => {
  const images = [
    gallows0,
    gallows1,
    gallows2,
    gallows3,
    gallows4,
    gallows5,
    gallows6,
  ];
  const [activeWord, setActiveWord] = useState("");
  const [guesses, setGuesses] = useState({
    incorrectGuesses: 0,
    correctGuesses: [],
    availableGuesses: 6,
  });
  const [inactiveLetters, setInactiveLetters] = useState([]);
  const [previousQuestion, setPreviousQuestion] = useState({});
  const [gameStatus, setGameStatus] = useState("playing"); // 'playing', 'win', 'lose'
  const [hint, setHint] = useState("");

  // Initialize game
  const placeWord = useCallback(
    (prevQuestion) => {
      const index = Math.floor(Math.random() * HANGMAN_QUESTIONS.length);
      const newQuestion = HANGMAN_QUESTIONS[index];

      if (
        prevQuestion.hint === newQuestion.hint &&
        prevQuestion.answer === newQuestion.answer
      ) {
        console.log(
          "Ooops. Randomizer tried to place the same word again. Retrying..."
        );
        placeWord(previousQuestion);
        return;
      }

      setPreviousQuestion(newQuestion);
      setActiveWord(newQuestion.answer);
      setHint(newQuestion.hint);
      // console.log(newQuestion.answer);
    },
    [previousQuestion]
  );

  // Reset game
  const resetGame = useCallback(() => {
    setGuesses({
      incorrectGuesses: 0,
      correctGuesses: [],
      availableGuesses: 6,
    });
    setInactiveLetters([]);
    setGameStatus("playing");
    placeWord(previousQuestion);
  }, [placeWord, previousQuestion]);

  // Initialize game on component mount
  useEffect(() => {
    placeWord({});
  }, [placeWord]);

  // Check for win/lose conditions
  useEffect(() => {
    if (
      guesses.correctGuesses.length === activeWord.length &&
      activeWord.length > 0
    ) {
      setGameStatus("win");
    } else if (guesses.incorrectGuesses >= guesses.availableGuesses) {
      setGameStatus("lose");
    }
  }, [
    guesses.correctGuesses.length,
    guesses.incorrectGuesses,
    guesses.availableGuesses,
    activeWord.length,
  ]);

  // Handle keyboard input
  useEffect(() => {
    const handleKeyDown = (e) => {
      const regex = /[A-Za-z]/g;
      const regexCode = /^Key/g;
      const key = e.code.slice(-1).toLowerCase();

      if (
        e.key.length === 1 &&
        e.code.match(regexCode) &&
        key.match(regex) &&
        !inactiveLetters.includes(key) &&
        gameStatus === "playing"
      ) {
        checkLetter(key);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [inactiveLetters, gameStatus]);

  const checkLetter = (key) => {
    const lowerKey = key.toLowerCase();

    if (!inactiveLetters.includes(lowerKey) && activeWord.includes(lowerKey)) {
      setGuesses((prev) => ({
        ...prev,
        correctGuesses: [...prev.correctGuesses, lowerKey],
      }));
    } else {
      setGuesses((prev) => ({
        ...prev,
        incorrectGuesses: prev.incorrectGuesses + 1,
      }));
    }

    setInactiveLetters((prev) => [...prev, lowerKey]);
  };

  const handleKeyClick = (letter) => {
    if (
      !inactiveLetters.includes(letter.toLowerCase()) &&
      gameStatus === "playing"
    ) {
      checkLetter(letter);
    }
  };

  // Render guess letters
  const renderGuessLetters = () => {
    return activeWord.split("").map((letter, index) => (
      <div key={index} className='guess__letter'>
        {guesses.correctGuesses.includes(letter) ? letter.toUpperCase() : ""}
      </div>
    ));
  };

  // Render keyboard
  const renderKeyboard = () => {
    return ALPHABET.map((item, index) => (
      <button
        key={index}
        id={`Key${ALPHABET[index].letter.toUpperCase()}`}
        className={`keyboard__key ${
          inactiveLetters.includes(item.letter.toLowerCase())
            ? "keyboard__key_inactive"
            : ""
        }`}
        onClick={() => handleKeyClick(item.letter)}
        disabled={
          inactiveLetters.includes(item.letter.toLowerCase()) ||
          gameStatus !== "playing"
        }
      >
        {item.letter}
      </button>
    ));
  };

  return (
    <div id='hangman-page'>
      <main className='wrapper'>
        <div className='gallows'>
          <div
            className='gallows__bg-image'
            style={{
              backgroundImage: `url(${images[guesses.incorrectGuesses]})`,
            }}
          />
          <h1 className='gallows__title'>Hangman game</h1>
        </div>

        <div className='task'>
          <div className='guess'>{renderGuessLetters()}</div>

          <div className='hint'>{hint}</div>

          <div className='task__guess-count'>
            Incorrect guesses {guesses.incorrectGuesses} /{" "}
            {guesses.availableGuesses}
          </div>

          <div className='keyboard'>{renderKeyboard()}</div>
        </div>
      </main>

      {/* Popup */}
      {gameStatus !== "playing" && (
        <div
          className='popup'
          onClick={(e) => {
            if (!e.target.closest(".popup__wrapper")) {
              resetGame();
            }
          }}
        >
          <div className='popup__wrapper'>
            <h2 className='popup__title'>
              {gameStatus === "win" ? "You WIN!" : "You LOSE!"}
            </h2>
            <p className='popup__answer'>
              The answer is: {activeWord.toUpperCase()}
            </p>
            <button className='popup__start-again' onClick={resetGame}>
              START AGAIN
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Hangman;
